import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import * as EXIF from 'exif-js';

@Injectable({
  providedIn: 'root',
})
export class FileConvertService {
  public b64toFormData(b64String: string, fieldName = 'file'): FormData {
    const contentType = this.getBase64ContentType(b64String);
    const realData = this.getBase64Data(b64String);
    const blob = this.b64toBlob(realData, contentType);
    const file = new File([blob], 'provider-image.png');

    const formData = new FormData();
    formData.append(fieldName, file);
    return formData;
  }

  public downloadFile(data: Blob, filename: string) {
    const blob = new Blob([data], { type: 'application/pdf' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  public b64toFile(b64String: string, fieldName = 'file'): File {
    const contentType = this.getBase64ContentType(b64String);
    const realData = this.getBase64Data(b64String);
    const blob = this.b64toBlob(realData, contentType);
    const file = new File([blob], 'provider-image.png');
    return file;
  }

  // @TODO this method works for convert file
  // need to leave only one
  public b64toFile2(dataurl: string, fieldName = 'file') {
    const arr: any = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fieldName, { type: mime });
  }

  public b64toBlob(b64Data: string, contentType?, sliceSize?): Blob {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;

    let byteCharacters = b64Data;
    try {
      byteCharacters = atob(b64Data);
    } catch (e) {
      console.log('SKIPPING: Failure to decode base64 string');
    }

    const byteArrays: any = [];

    for (let offset = 0, length = byteCharacters.length; offset < length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  public toBase64viaCanvas(
    url: string,
    callback: (base64: string) => void,
    callbackError: (error: any) => void,
    maxWidth: number | any = 1920,
    maxHeight: number | any = 1920,
  ) {
    if (!url) {
      return callbackError('Please provide image URL');
    }
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // Calculate the new dimensions while maintaining the aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      // Create a canvas element with the new dimensions
      const canvas = document.createElement('canvas') as HTMLCanvasElement;
      canvas.width = width;
      canvas.height = height;

      // Draw the resized image onto the canvas
      const ctx: any = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Convert the canvas content to a Base64 string
      const base64 = canvas.toDataURL('image/jpeg');
      callback(base64);
    };
    img.onerror = (err) => {
      callbackError(err);
    };

    // Prevent cache to avoid CORS
    img.src = url + (url?.indexOf('?') === -1 ? '?' : '&') + 'h' + Date.now();
  }

  public resizeBase64Image(
    base64: string,
    callback: (resizedBase64: string) => void,
    callbackError: (error: any) => void,
    maxWidth: number | any = 1920,
    maxHeight: number | any = 1920,
  ) {
    if (!base64) {
      return callbackError('Please provide a Base64 image string');
    }

    const img = new Image();
    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // Calculate the new dimensions while maintaining the aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      // Create a canvas element with the new dimensions
      const canvas = document.createElement('canvas') as HTMLCanvasElement;
      canvas.width = width;
      canvas.height = height;

      // Draw the resized image onto the canvas
      const ctx: any = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Convert the canvas content to a Base64 string
      const resizedBase64 = canvas.toDataURL('image/jpeg');
      callback(resizedBase64);
    };

    img.onerror = (err) => {
      callbackError(err);
    };

    img.src = base64;
  }

  public toBase64viaCanvasObservable(
    url: any,
    maxWidth: number | any = 1920,
    maxHeight: number | any = 1920,
  ) {
    return new Observable((observer: Observer<any>) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Calculate the new dimensions while maintaining the aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        // Create a canvas element with the new dimensions
        const canvas = document.createElement('canvas') as HTMLCanvasElement;
        canvas.width = width;
        canvas.height = height;

        // Draw the resized image onto the canvas
        const ctx: any = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert the canvas content to a Base64 string
        const base64 = canvas.toDataURL('image/jpeg');
        observer.next(base64);
      };
      img.onerror = (err) => {
        observer.error(err);
      };

      // Prevent cache to avoid CORS
      img.src = url + (url.indexOf('?') === -1 ? '?' : '&') + 'h' + Date.now();
    });
  }

  private getBase64ContentType(b64Data: string): string {
    const block = b64Data.split(';');
    return block[0].split(':')[1];
  }

  private getBase64Data(b64Data: string): string {
    const block = b64Data.split(';');
    return block[1].split(',')[1];
  }
}
